import {
  EMAIL_MAX_LENGTH,
  EMAIL_TOO_LONG_MESSAGE,
  PHONE_MAX_LENGTH,
  PHONE_TOO_LONG_MESSAGE,
  RADY_EMAIL_INVALID_MESSAGE,
  RADY_EMAIL_MAX_LENGTH,
  RADY_EMAIL_REGEX,
  RADY_EMAIL_TOO_LONG_MESSAGE,
  RADY_PHONE_INVALID_MESSAGE,
  RADY_PHONE_MAX_LENGTH,
  RADY_PHONE_REGEX,
  RADY_PHONE_TOO_LONG_MESSAGE,
  isWithinLength,
} from '@/utils/freeTextValidation'
import { isPossiblePhoneNumber } from 'libphonenumber-js'
import validator from 'validator'

/** `rady` applies the Rady (RCIGM) intake limits on top of the DB caps. */
export interface ContactValidationOptions {
  rady?: boolean
}

/** Matches ordering flows: non-empty + `validator.isEmail` + within the DB length cap. */
export function isValidOrderContactEmail(
  value: string | undefined | null,
  options: ContactValidationOptions = {}
): boolean {
  return Boolean(value?.trim()) && orderContactEmailError(value, options) === undefined
}

/**
 * Accepts US national numbers (backward compatible) and international numbers in
 * E.164 / formatted form (e.g. `+44 20 7123 4567`, `+52 55 1234 5678`, `+86 139 1234 5678`).
 *
 * A leading `+` is checked against the country of its dialing code, so any country
 * (Mexican, Chinese, etc.) is accepted when the caller includes the international prefix.
 * A number without a `+` is interpreted as US only — international numbers MUST include
 * their `+` country code to validate. Uses libphonenumber-js `isPossiblePhoneNumber` so
 * validation only checks the number's form/length (not specific area codes or number
 * ranges), and formatting (spaces, dashes, parens) is tolerated. Also enforces the DB
 * length cap so hydrated/copied values that exceed the limit are rejected even though a
 * bare `maxLength` input can't catch them.
 */
export function isValidOrderContactPhone(
  value: string | undefined | null,
  options: ContactValidationOptions = {}
): boolean {
  return Boolean(value?.trim()) && orderContactPhoneError(value, options) === undefined
}

function isPossiblePhone(t: string): boolean {
  try {
    // Bare national numbers default to US; `+`-prefixed numbers carry their own country code.
    return t.startsWith('+') ? isPossiblePhoneNumber(t) : isPossiblePhoneNumber(t, 'US')
  } catch {
    return false
  }
}

export function orderContactEmailError(
  value: string | undefined | null,
  options: ContactValidationOptions = {}
): string | undefined {
  const t = value?.trim()
  if (!t) return 'Enter an email address'
  if (!isWithinLength(t, EMAIL_MAX_LENGTH)) return EMAIL_TOO_LONG_MESSAGE
  if (!validator.isEmail(t)) return 'Enter a valid email address'
  if (options.rady) {
    if (!isWithinLength(t, RADY_EMAIL_MAX_LENGTH)) return RADY_EMAIL_TOO_LONG_MESSAGE
    if (!RADY_EMAIL_REGEX.test(t)) return RADY_EMAIL_INVALID_MESSAGE
  }
  return undefined
}

export function orderContactPhoneError(
  value: string | undefined | null,
  options: ContactValidationOptions = {}
): string | undefined {
  const t = value?.trim()
  if (!t) return 'Enter a phone number'
  if (!isWithinLength(t, PHONE_MAX_LENGTH)) return PHONE_TOO_LONG_MESSAGE
  if (!isPossiblePhone(t))
    return 'Enter a valid phone number. For international numbers, include a country code with +.'
  if (options.rady) {
    if (!isWithinLength(t, RADY_PHONE_MAX_LENGTH)) return RADY_PHONE_TOO_LONG_MESSAGE
    if (!RADY_PHONE_REGEX.test(t)) return RADY_PHONE_INVALID_MESSAGE
  }
  return undefined
}

/**
 * Rady `OrderInfo` limit for the ordering provider email (`physician_email` / `provider_email`
 * max 50). Profile emails allow more, and an over-limit value would otherwise be swapped for
 * a placeholder before it reaches Rady.
 */
export function radyOrderingProviderContactError(
  provider: { email?: string | null } | null | undefined
): string | null {
  const email = provider?.email?.trim()
  if (email && !isWithinLength(email, RADY_EMAIL_MAX_LENGTH)) {
    return `Your profile email must be ${RADY_EMAIL_MAX_LENGTH} characters or fewer for Rady orders. Update your profile and try again.`
  }
  return null
}

/** US-style phone/fax: 10 digits, or 11 with leading country code 1. */
export function isValidUsPhoneDigits(value: string | undefined | null): boolean {
  const digits = String(value ?? '').replace(/\D/g, '')
  if (digits.length === 10) return true
  if (digits.length === 11 && digits[0] === '1') return true
  return false
}
